import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Eye, EyeOff, LockKeyhole, LogIn, Mail, MessageCircle, RotateCcw, ShieldCheck, UserRound, UserRoundPlus } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";

type RegistrationMethod = "email" | "local";

function safeNextPath(value: string | null): string {
  return value?.startsWith("/") && !value.startsWith("//") && !value.includes("\\") ? value : "/square";
}

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const {
    user, config, login, register, requestEmailRegistration, confirmEmailRegistration,
    requestPasswordReset, confirmPasswordReset, exchangeQqTicket
  } = useApp();
  const [identifier, setIdentifier] = useState("");
  const [account, setAccount] = useState("");
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [registrationMethod, setRegistrationMethod] = useState<RegistrationMethod>("email");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetVerificationId, setResetVerificationId] = useState<string | null>(null);
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const callbackHandled = useRef(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const nextPath = useMemo(() => safeNextPath(searchParams.get("next")), [searchParams]);
  const methods = config?.authMethods;
  const authHero = config?.authHero ?? {
    title: "真实账号实拍\n交流留在站内",
    description: "登录后可以点赞、查看号商主页、发送站内私信并加入官方群。所有购买、付款和售后统一前往商城完成。",
    images: ["/assets/demo/account-space.png", "/assets/demo/account-fantasy.png"]
  };
  const accessNotice = useMemo(() => {
    if (nextPath === "/post/new") return "请先登录账号，才可以发布动态内容。";
    if (nextPath.startsWith("/messages")) return "请先登录账号，才可以发送或查看私信。";
    if (nextPath.startsWith("/groups/")) return "请先登录账号，才可以进入官方群。";
    return null;
  }, [nextPath]);

  useEffect(() => {
    setError(null);
    setNotice(null);
    setVerificationId(null);
    setVerificationCode("");
    setResetMode(false);
  }, [mode]);

  useEffect(() => {
    if (methods && !methods.emailRegistrationEnabled) setRegistrationMethod("local");
  }, [methods]);

  useEffect(() => {
    const ticket = searchParams.get("qq_ticket");
    if (!ticket || callbackHandled.current) return;
    callbackHandled.current = true;
    setBusy(true);
    setError(null);
    void exchangeQqTicket(ticket)
      .then((nextUser) => navigate(nextUser.onboardingRequired ? "/account?setup=email" : nextPath, { replace: true }))
      .catch((callbackError) => {
        setError(callbackError instanceof Error ? callbackError.message : "QQ 登录失败，请重新尝试");
        setBusy(false);
      });
  }, [exchangeQqTicket, navigate, nextPath, searchParams]);

  if (user) return <Navigate to={user.onboardingRequired ? "/account?setup=email" : nextPath} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "login") {
        await login(identifier, password);
        navigate(nextPath, { replace: true });
      } else if (registrationMethod === "email") {
        if (!verificationId) {
          const result = await requestEmailRegistration(account, password, nickname, email);
          setVerificationId(result.verificationId);
          setNotice(`验证码已发送到 ${email}，${Math.round(result.expiresInSeconds / 60)} 分钟内有效。`);
        } else {
          await confirmEmailRegistration(verificationId, verificationCode);
          navigate(nextPath, { replace: true });
        }
      } else {
        await register(account, password, nickname);
        navigate(nextPath, { replace: true });
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "操作失败，请稍后重试");
    } finally { setBusy(false); }
  };

  const submitReset = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (!resetVerificationId) {
        const result = await requestPasswordReset(resetEmail);
        setResetVerificationId(result.verificationId);
        setNotice("如果该邮箱已完成绑定，验证码邮件已经发送。");
      } else {
        if (resetPassword !== resetPasswordConfirm) throw new Error("两次输入的新密码不一致");
        await confirmPasswordReset(resetVerificationId, resetCode, resetPassword);
        setNotice("密码已重置，请使用新密码登录。");
        setResetMode(false);
        setIdentifier(resetEmail);
        setPassword("");
      }
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : "密码找回失败，请稍后重试");
    } finally { setBusy(false); }
  };

  const startQqLogin = () => {
    window.location.assign(`/api/auth/qq/start?next=${encodeURIComponent(nextPath)}`);
  };

  const demoAccounts = [
    { label: "玩家", account: "player", password: "Player123!Demo" },
    { label: "号商", account: "seller", password: "Seller123!Demo" },
    { label: "管理员", account: "admin", password: "Admin123!Demo" }
  ];

  return (
    <div className="auth-page page-shell">
      <section className="auth-context t-stagger" aria-labelledby="auth-context-title">
        <h1 id="auth-context-title" className="t-stagger-line">{authHero.title}</h1>
        <p className="t-stagger-line">{authHero.description}</p>
        <div className="auth-image-strip t-stagger-line">{authHero.images.slice(0, 2).map((imageUrl, index) => <img key={`${imageUrl}-${index}`} src={imageUrl} alt={`认证页${index === 0 ? "主图" : "辅图"}`} />)}</div>
      </section>
      <section className={`auth-form-wrap${error ? " is-error" : ""}`} aria-labelledby="auth-form-title" aria-busy={busy}>
        <div className="auth-form-heading"><span className="auth-icon">{resetMode ? <RotateCcw aria-hidden="true" /> : mode === "login" ? <LogIn aria-hidden="true" /> : <UserRoundPlus aria-hidden="true" />}</span><div><h1 id="auth-form-title">{resetMode ? "找回密码" : mode === "login" ? "登录" : "注册"}</h1><p>{resetMode ? "通过已验证邮箱设置新密码" : mode === "login" ? "使用站内账号、邮箱或 QQ 继续" : "创建玩家账号，交易仍统一前往商城"}</p></div></div>
        {mode === "login" && accessNotice && !resetMode ? <Alert severity="info">{accessNotice} 登录完成后会返回原页面。</Alert> : null}
        {error ? <Alert severity="error" aria-live="assertive">{error}，已填写内容仍保留，请修改后重试。</Alert> : null}
        {notice ? <Alert severity="success" icon={<ShieldCheck aria-hidden="true" />} aria-live="polite">{notice}</Alert> : null}

        {resetMode ? <Stack component="form" spacing={2} onSubmit={(event) => void submitReset(event)}>
          <TextField label="已绑定邮箱" type="email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} required disabled={Boolean(resetVerificationId)} autoComplete="email" slotProps={{ input: { startAdornment: <InputAdornment position="start"><Mail aria-hidden="true" /></InputAdornment> } }} />
          {resetVerificationId ? <>
            <TextField label="邮箱验证码" value={resetCode} onChange={(event) => setResetCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]{6}", autoComplete: "one-time-code" } }} />
            <TextField label="新密码" type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} required helperText="至少 10 位字符" slotProps={{ htmlInput: { minLength: 10, maxLength: 72, autoComplete: "new-password" } }} />
            <TextField label="确认新密码" type="password" value={resetPasswordConfirm} onChange={(event) => setResetPasswordConfirm(event.target.value)} required slotProps={{ htmlInput: { minLength: 10, maxLength: 72, autoComplete: "new-password" } }} />
          </> : null}
          <Button className="button-full" variant="contained" type="submit" disabled={busy} startIcon={busy ? <CircularProgress color="inherit" size={16} /> : <RotateCcw aria-hidden="true" />}>{busy ? "正在处理" : resetVerificationId ? "验证并重置密码" : "发送验证码"}</Button>
          <Button type="button" variant="text" onClick={() => { setResetMode(false); setError(null); setResetVerificationId(null); }}>返回登录</Button>
        </Stack> : <>
          {mode === "register" && methods?.emailRegistrationEnabled ? <ToggleButtonGroup className="auth-method-switch" exclusive fullWidth value={registrationMethod} onChange={(_event, value: RegistrationMethod | null) => value && setRegistrationMethod(value)} aria-label="注册方式">
            <ToggleButton value="email"><Mail aria-hidden="true" />邮箱验证注册</ToggleButton>
            <ToggleButton value="local"><UserRound aria-hidden="true" />本地账号注册</ToggleButton>
          </ToggleButtonGroup> : null}
          <Stack component="form" spacing={2} onSubmit={(event) => void submit(event)}>
            {mode === "register" ? <>
              <TextField label="昵称" value={nickname} onChange={(event) => setNickname(event.target.value)} required disabled={Boolean(verificationId)} slotProps={{ input: { startAdornment: <InputAdornment position="start"><UserRound aria-hidden="true" /></InputAdornment> }, htmlInput: { minLength: 2, maxLength: 24, autoComplete: "nickname" } }} />
              <TextField label="账号" value={account} onChange={(event) => setAccount(event.target.value)} required disabled={Boolean(verificationId)} helperText="3-32 位，可使用字母、数字、点、下划线和短横线" slotProps={{ input: { startAdornment: <InputAdornment position="start"><UserRound aria-hidden="true" /></InputAdornment> }, htmlInput: { minLength: 3, maxLength: 32, autoComplete: "username" } }} />
              {registrationMethod === "email" ? <TextField label="邮箱" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={Boolean(verificationId)} autoComplete="email" slotProps={{ input: { startAdornment: <InputAdornment position="start"><Mail aria-hidden="true" /></InputAdornment> } }} /> : null}
            </> : <TextField label="账号或邮箱" value={identifier} onChange={(event) => setIdentifier(event.target.value)} required slotProps={{ input: { startAdornment: <InputAdornment position="start"><UserRound aria-hidden="true" /></InputAdornment> }, htmlInput: { minLength: 3, maxLength: 254, autoComplete: "username" } }} />}
            <TextField label="密码" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} required disabled={Boolean(verificationId)} helperText={mode === "register" ? "至少 10 位字符" : undefined} slotProps={{ input: { startAdornment: <InputAdornment position="start"><LockKeyhole aria-hidden="true" /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton edge="end" type="button" aria-label={showPassword ? "隐藏密码" : "显示密码"} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}</IconButton></InputAdornment> }, htmlInput: { minLength: 10, maxLength: 72, autoComplete: mode === "login" ? "current-password" : "new-password" } }} />
            {verificationId ? <TextField label="邮箱验证码" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]{6}", autoComplete: "one-time-code" } }} /> : null}
            <Button className="button-full" variant="contained" type="submit" disabled={busy} startIcon={busy ? <CircularProgress color="inherit" size={16} /> : mode === "login" ? <LogIn aria-hidden="true" /> : verificationId ? <ShieldCheck aria-hidden="true" /> : <UserRoundPlus aria-hidden="true" />}>{busy ? "正在处理" : mode === "login" ? "登录" : verificationId ? "验证并注册" : registrationMethod === "email" ? "发送验证码" : "注册并登录"}</Button>
            {verificationId ? <Button type="button" variant="text" onClick={() => { setVerificationId(null); setVerificationCode(""); setNotice(null); }}>修改注册信息</Button> : null}
          </Stack>

          {mode === "login" ? <Button className="auth-forgot-link" type="button" variant="text" onClick={() => { setResetMode(true); setError(null); setNotice(null); }}>忘记密码？</Button> : null}
          {methods?.qqLoginEnabled ? <><Divider className="auth-divider">其他方式</Divider><Button className="qq-login-button" variant="outlined" type="button" disabled={busy} startIcon={<MessageCircle aria-hidden="true" />} onClick={startQqLogin}>使用 QQ 登录</Button><p className="auth-provider-note">QQ 首次登录后需要绑定邮箱，平台不会保存远程头像。</p></> : null}
          {import.meta.env.DEV && mode === "login" ? <div className="demo-accounts"><span>本地演示身份</span><div>{demoAccounts.map((item) => <Button variant="text" size="small" type="button" key={item.label} onClick={() => { setIdentifier(item.account); setPassword(item.password); }}>{item.label}</Button>)}</div></div> : null}
          <p className="auth-switch">{mode === "login" ? <>还没有账号？<Link to="/register">注册</Link></> : <>已有账号？<Link to="/login">返回登录</Link></>}</p>
        </>}
      </section>
    </div>
  );
}
